import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Person, PersonFormData, PersonSearchResult, ValidationResult, PersonSummary, UserRole } from '../types';
import { ChangeTrackingService } from './changeTrackingService';
import { ProfileValidationService } from './profileValidationService';
import { ValidationService } from './validationService';

/**
 * PersonService - Handles all person-related operations with Firestore
 * Implements comprehensive CRUD operations, search, validation, and caching
 */
export class PersonService {
  private static readonly COLLECTION_NAME = 'persons';

  /**
   * Check if user can edit a specific person
   * Returns true if:
   * - User is admin (can edit anyone)
   * - User has editor role (can edit anyone)
   * - User is linked to this specific person (can edit own profile)
   *
   * @param {string} userId - User ID
   * @param {string} personId - Person ID to check
   * @param {UserRole} userRole - User's role
   * @returns {Promise<boolean>} True if user can edit
   */
  static async canUserEditPerson(
    userId: string,
    personId: string,
    userRole: UserRole
  ): Promise<boolean> {
    try {
      // Admins and editors can edit anyone
      if (userRole === 'admin' || userRole === 'editor') {
        return true;
      }

      // Check if user is linked to this person
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return false;
      }

      const linkedPersonId = userDoc.data().personId;

      // User can edit their own linked person profile
      if (linkedPersonId === personId) {
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('Error checking edit permission:', error);
      return false;
    }
  }

  /**
   * Create a new person record
   */
  static async createPerson(
    personData: PersonFormData,
    userId: string,
    userName: string = 'Unknown User',
    isAdmin: boolean = false
  ): Promise<Person> {
    try {
      // Normalize data (auto-mark deceased, etc.)
      const normalizedData = ProfileValidationService.validateAndNormalize(personData);

      // Validate input data
      const validation = ValidationService.validatePersonForm(normalizedData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
      }

      // Prepare person object (only using fields available in PersonFormData)
      const personDoc = {
        firstName: normalizedData.firstName,
        lastName: normalizedData.lastName,
        maidenName: normalizedData.maidenName || undefined,
        middleName: normalizedData.middleName || undefined,
        suffix: normalizedData.suffix || undefined,
        nicknames: normalizedData.nicknames ? normalizedData.nicknames.split(',').map((n: string) => n.trim()).filter((n: string) => n) : [],
        gender: normalizedData.gender,
        birthDate: normalizedData.birthDate || undefined,
        deathDate: normalizedData.deathDate || undefined,
        isDeceased: normalizedData.isDeceased || false,
        birthPlace: normalizedData.birthPlace || undefined,
        deathPlace: normalizedData.deathPlace || undefined,
        currentLocation: normalizedData.currentLocation || undefined,
        occupation: normalizedData.occupation || undefined,
        education: normalizedData.education || undefined,
        biography: normalizedData.biography || undefined,
        personalNotes: normalizedData.personalNotes || undefined,
        // Initialize fields not in PersonFormData with defaults
        occupations: [],
        militaryService: undefined,
        physicalDescription: undefined,
        personality: undefined,
        interests: [],
        achievements: [],
        primaryPhotoId: undefined,
        photoIds: [],
        documentIds: [],
        storyIds: [],
        isPrivate: normalizedData.isPrivate || false,
        isPublic: false,
        completeness: await ProfileValidationService.calculateCompleteness(normalizedData),
        sources: [],
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        id: '', // Will be set after creation
      };

      // Create document in Firestore
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), personDoc);

      // Update document with its own ID
      await updateDoc(docRef, { id: docRef.id });

      // Build the created person object
      const createdPerson: Person = {
        ...personDoc,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as Person;

      // Log change to ChangeTrackingService
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'person',
            entityId: docRef.id,
            changeType: 'create',
            beforeState: null,
            afterState: createdPerson,
            changedFields: Object.keys(personDoc),
            reason: `Person created: ${personData.firstName} ${personData.lastName}`,
          },
          userId,
          userName,
          isAdmin,
          'user' // Source: manual user edit
        );
      } catch (changeLogError: any) {
        console.error('Failed to log change (non-blocking):', changeLogError);
        // Don't throw - change logging is non-critical
      }

      // Return the created person
      return createdPerson;
    } catch (error: any) {
      throw new Error(`Failed to create person: ${error.message}`);
    }
  }

  /**
   * Update an existing person record
   */
  static async updatePerson(
    personId: string,
    updates: Partial<Person>,
    userId: string,
    userName: string = 'Unknown User',
    isAdmin: boolean = false,
    userRole: UserRole = 'viewer'
  ): Promise<void> {
    try {
      // Check permission
      const canEdit = await this.canUserEditPerson(userId, personId, userRole);
      if (!canEdit) {
        throw new Error('You do not have permission to edit this person');
      }

      const personRef = doc(db, this.COLLECTION_NAME, personId);

      // Verify person exists
      const personDoc = await getDoc(personRef);
      if (!personDoc.exists()) {
        throw new Error('Person not found');
      }

      // Get before state for change tracking
      const beforeState = {
        id: personDoc.id,
        ...personDoc.data(),
      } as Person;

      // Merge current data with updates for validation
      const currentData = personDoc.data();
      const mergedForValidation: any = { ...currentData, ...updates };

      // Convert to PersonFormData for validation
      const formData = this.personToFormData(mergedForValidation as Person);

      // Normalize data (auto-mark deceased, etc.)
      const normalizedData = ProfileValidationService.validateAndNormalize(formData);

      // Validate merged data
      const validation = ValidationService.validatePersonForm(normalizedData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
      }

      // Prepare update object
      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
        updatedByName: userName,  // ✅ CRITICAL FIX: Cache user name for History tab
      };

      // Recalculate completeness FIRST if personal data changed (before converting undefined to deleteField)
      if (this.hasPersonalDataChanges(updates)) {
        const currentData = personDoc.data();
        // For calculation, treat undefined as actually removed
        const mergedData: any = { ...currentData };
        Object.keys(updates).forEach(key => {
          if (updates[key as keyof Person] === undefined) {
            // Field is being deleted, so remove it from merged data
            delete mergedData[key];
          } else {
            mergedData[key] = updates[key as keyof Person];
          }
        });
        const updatedFormData = this.personToFormData(mergedData as Person);
        updateData.completeness = await ProfileValidationService.calculateCompleteness(updatedFormData);
      }

      // NOW convert undefined values to deleteField() so they're removed from Firestore
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          updateData[key] = deleteField();
        }
      });

      await updateDoc(personRef, updateData);

      // Get after state for change tracking
      const afterState = {
        ...beforeState,
        ...updateData,
        updatedAt: new Date(),
      } as Person;

      // Log change to ChangeTrackingService
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'person',
            entityId: personId,
            changeType: 'update',
            beforeState,
            afterState,
            changedFields: Object.keys(updates),
            reason: `Person updated: ${beforeState.firstName} ${beforeState.lastName}`,
          },
          userId,
          userName,
          isAdmin,
          'user' // Source: manual user edit
        );
      } catch (changeLogError: any) {
        console.error('Failed to log change (non-blocking):', changeLogError);
        // Don't throw - change logging is non-critical
      }
    } catch (error: any) {
      throw new Error(`Failed to update person: ${error.message}`);
    }
  }

  /**
   * Delete a person record (soft delete)
   */
  static async deletePerson(
    personId: string,
    userId: string,
    userName: string = 'Unknown User',
    isAdmin: boolean = false
  ): Promise<void> {
    try {
      const personRef = doc(db, this.COLLECTION_NAME, personId);

      // Get person data before deletion for change tracking
      const personDoc = await getDoc(personRef);
      if (!personDoc.exists()) {
        throw new Error('Person not found');
      }

      const beforeState = {
        id: personDoc.id,
        ...personDoc.data(),
      } as Person;

      // Soft delete by marking as deleted
      await updateDoc(personRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: userId,
      });

      // Log change to ChangeTrackingService
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'person',
            entityId: personId,
            changeType: 'delete',
            beforeState,
            afterState: null,
            changedFields: ['isDeleted', 'deletedAt', 'deletedBy'],
            reason: `Person deleted: ${beforeState.firstName} ${beforeState.lastName}`,
          },
          userId,
          userName,
          isAdmin,
          'user' // Source: manual user edit
        );
      } catch (changeLogError: any) {
        console.error('Failed to log change (non-blocking):', changeLogError);
        // Don't throw - change logging is non-critical
      }
    } catch (error: any) {
      throw new Error(`Failed to delete person: ${error.message}`);
    }
  }

  /**
   * Get person by ID
   */
  static async getPersonById(personId: string): Promise<Person | null> {
    try {
      const personDoc = await getDoc(doc(db, this.COLLECTION_NAME, personId));
      
      if (!personDoc.exists()) {
        return null;
      }

      const data = personDoc.data();
      
      // Convert Firestore timestamps to Date objects, handle both Timestamp and string formats
      const createdAt = data.createdAt
        ? (typeof data.createdAt === 'string' ? new Date(data.createdAt) : data.createdAt.toDate?.() || new Date(data.createdAt))
        : new Date();
      
      const updatedAt = data.updatedAt
        ? (typeof data.updatedAt === 'string' ? new Date(data.updatedAt) : data.updatedAt.toDate?.() || new Date(data.updatedAt))
        : new Date();

      return {
        ...data,
        id: personDoc.id,
        createdAt,
        updatedAt,
      } as Person;
    } catch (error: any) {
      // Handle specific Firebase errors gracefully
      if (error.code === 'permission-denied' ||
          error.code === 'not-found' ||
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions')) {
        // Return null for permission issues or missing collections
        // This allows the app to handle empty databases gracefully
        return null;
      }
      
      // Re-throw unexpected errors
      throw new Error(`Failed to get person: ${error.message}`);
    }
  }

  /**
   * Get all persons (with pagination)
   */
  static async getPersons(pageSize: number = 50, lastDoc?: DocumentSnapshot): Promise<{
    persons: Person[];
    lastDoc: DocumentSnapshot | null;
    hasMore: boolean;
  }> {
    try {
      let q = query(
        collection(db, this.COLLECTION_NAME),
        where('isDeleted', '!=', true),
        orderBy('isDeleted'),
        orderBy('lastName'),
        orderBy('firstName'),
        limit(pageSize + 1) // Get one extra to check if there are more
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;
      
      // Remove the extra document if present
      if (hasMore) {
        docs.pop();
      }

      const persons = docs.map(doc => {
        const data = doc.data();
        
        // Convert Firestore timestamps to Date objects, handle both Timestamp and string formats
        const createdAt = data.createdAt
          ? (typeof data.createdAt === 'string' ? new Date(data.createdAt) : data.createdAt.toDate?.() || new Date(data.createdAt))
          : new Date();
        
        const updatedAt = data.updatedAt
          ? (typeof data.updatedAt === 'string' ? new Date(data.updatedAt) : data.updatedAt.toDate?.() || new Date(data.updatedAt))
          : new Date();

        return {
          ...data,
          id: doc.id,
          createdAt,
          updatedAt,
        } as Person;
      });

      return {
        persons,
        lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
        hasMore,
      };
    } catch (error: any) {
      // Handle specific Firebase errors gracefully
      if (error.code === 'permission-denied' ||
          error.code === 'not-found' ||
          error.code === 'failed-precondition' ||
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('requires an index')) {
        // Return empty result for permission issues, missing collections, or index errors
        return {
          persons: [],
          lastDoc: null,
          hasMore: false,
        };
      }
      
      // Re-throw unexpected errors
      throw new Error(`Failed to get persons: ${error.message}`);
    }
  }

  /**
   * Search persons by name and filters
   */
  static async searchPersons(searchTerm: string, filters?: {
    gender?: string;
    isDeceased?: boolean;
    birthYear?: { from?: number; to?: number };
  }): Promise<PersonSearchResult[]> {
    try {
      let q = query(
        collection(db, this.COLLECTION_NAME),
        where('isDeleted', '==', false),
        orderBy('lastName')
      );

      // Apply filters
      if (filters?.gender) {
        q = query(q, where('gender', '==', filters.gender));
      }
      
      if (filters?.isDeceased !== undefined) {
        q = query(q, where('isDeceased', '==', filters.isDeceased));
      }

      const querySnapshot = await getDocs(q);
      const persons = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
        } as Person;
      });

      // Client-side filtering for complex search
      const searchResults = persons
        .filter(person => {
          const fullName = `${person.firstName} ${person.lastName} ${person.maidenName || ''}`.toLowerCase();
          const searchLower = searchTerm.toLowerCase();
          
          const nameMatch = fullName.includes(searchLower);
          const nicknameMatch = person.nicknames?.some((nickname: string) =>
            nickname.toLowerCase().includes(searchLower)
          );
          
          let match = nameMatch || nicknameMatch;

          // Apply birth year filter
          if (match && filters?.birthYear) {
            const birthYear = person.birthDate ? new Date(person.birthDate).getFullYear() : null;
            if (birthYear) {
              if (filters.birthYear.from && birthYear < filters.birthYear.from) match = false;
              if (filters.birthYear.to && birthYear > filters.birthYear.to) match = false;
            }
          }
          
          return match;
        })
        .map(person => ({
          id: person.id,
          firstName: person.firstName,
          lastName: person.lastName,
          maidenName: person.maidenName,
          birthDate: person.birthDate,
          deathDate: person.deathDate,
          birthPlace: person.birthPlace?.city,
          primaryPhotoUrl: person.primaryPhotoId,
          relevanceScore: this.calculateRelevanceScore(person, searchTerm),
          matchedFields: this.getMatchedFields(person, searchTerm),
        }))
        .sort((a, b) => b.relevanceScore - a.relevanceScore);

      return searchResults;
    } catch (error: any) {
      // Handle specific Firebase errors gracefully
      if (error.code === 'permission-denied' ||
          error.code === 'not-found' ||
          error.code === 'failed-precondition' ||
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('requires an index')) {
        // Return empty results for permission issues, missing collections, or index errors
        return [];
      }
      
      // Re-throw unexpected errors
      throw new Error(`Failed to search persons: ${error.message}`);
    }
  }

  /**
   * Get person summary for relationship displays
   */
  static async getPersonSummary(personId: string): Promise<PersonSummary | null> {
    try {
      const person = await this.getPersonById(personId);
      if (!person) return null;

      return {
        id: person.id,
        firstName: person.firstName,
        lastName: person.lastName,
        maidenName: person.maidenName,
        birthDate: person.birthDate,
        deathDate: person.deathDate,
        isDeceased: person.isDeceased,
        primaryPhotoUrl: person.primaryPhotoId,
        gender: person.gender,
      };
    } catch (error: any) {
      throw new Error(`Failed to get person summary: ${error.message}`);
    }
  }

  /**
   * Find the oldest ancestor in the database
   * Useful for auto-detecting tree root
   * (Migrated from TreeBuildingService - now handled by TreeRelationshipService)
   *
   * @returns Oldest person by birth date, or null if no people exist
   */
  static async findOldestAncestor(): Promise<Person | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('isDeleted', '==', false),
        orderBy('birthDate', 'asc')
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      // Get first person with a birth date
      for (const doc of snapshot.docs) {
        const data = doc.data();
        if (data.birthDate) {
          return {
            ...data,
            id: doc.id,
            createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
            updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
          } as Person;
        }
      }

      // If no person has a birth date, return the first person
      const firstDoc = snapshot.docs[0];
      const data = firstDoc.data();
      return {
        ...data,
        id: firstDoc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
      } as Person;
    } catch (error: any) {
      console.error('Failed to find oldest ancestor:', error);
      return null;
    }
  }

  // Validation methods removed - use ValidationService.validatePersonForm() and ProfileValidationService instead

  /**
   * Calculate search relevance score
   */
  private static calculateRelevanceScore(person: Person, searchTerm: string): number {
    const searchLower = searchTerm.toLowerCase();
    let score = 0;

    // Exact matches get highest score
    if (person.firstName.toLowerCase() === searchLower) score += 100;
    if (person.lastName.toLowerCase() === searchLower) score += 100;
    
    // Partial matches
    if (person.firstName.toLowerCase().includes(searchLower)) score += 50;
    if (person.lastName.toLowerCase().includes(searchLower)) score += 50;
    if (person.maidenName?.toLowerCase().includes(searchLower)) score += 30;
    
    // Nickname matches
    person.nicknames?.forEach((nickname: string) => {
      if (nickname.toLowerCase().includes(searchLower)) score += 40;
    });

    return score;
  }

  /**
   * Get matched fields for search highlighting
   */
  private static getMatchedFields(person: Person, searchTerm: string): string[] {
    const searchLower = searchTerm.toLowerCase();
    const matchedFields: string[] = [];

    if (person.firstName.toLowerCase().includes(searchLower)) {
      matchedFields.push('firstName');
    }
    if (person.lastName.toLowerCase().includes(searchLower)) {
      matchedFields.push('lastName');
    }
    if (person.maidenName?.toLowerCase().includes(searchLower)) {
      matchedFields.push('maidenName');
    }
    
    person.nicknames?.forEach((nickname: string, index: number) => {
      if (nickname.toLowerCase().includes(searchLower)) {
        matchedFields.push(`nicknames.${index}`);
      }
    });

    return matchedFields;
  }

  /**
   * Check if updates contain personal data changes that affect completeness
   */
  private static hasPersonalDataChanges(updates: Partial<Person>): boolean {
    const personalDataFields = [
      'firstName', 'lastName', 'maidenName', 'middleName', 'nicknames',
      'gender', 'birthDate', 'deathDate', 'birthPlace', 'deathPlace',
      'occupation', 'education', 'biography', 'personalNotes',
      'interests', 'achievements'
    ];

    return personalDataFields.some(field => field in updates);
  }

  /**
   * Convert Person to PersonFormData for editing
   */
  private static personToFormData(person: Person): PersonFormData {
    return {
      firstName: person.firstName,
      lastName: person.lastName,
      maidenName: person.maidenName,
      middleName: person.middleName,
      suffix: person.suffix,
      nicknames: person.nicknames ? person.nicknames.join(', ') : '',
      gender: person.gender,
      birthDate: person.birthDate,
      deathDate: person.deathDate,
      isDeceased: person.isDeceased,
      birthPlace: person.birthPlace || { city: '', state: '', country: '' },
      deathPlace: person.deathPlace || { city: '', state: '', country: '' },
      currentLocation: person.currentLocation || { city: '', state: '', country: '' },
      occupation: person.occupation,
      education: person.education,
      biography: person.biography,
      personalNotes: person.personalNotes,
      isPrivate: person.isPrivate,
    };
  }

  /**
   * Get total count of non-deleted persons
   * Optimized for dashboard statistics
   *
   * @returns {Promise<number>} Total person count
   * @throws {Error} If count fails
   */
  static async getPersonCount(): Promise<number> {
    try {
      const personsRef = collection(db, this.COLLECTION_NAME);
      const q = query(personsRef, where('isDeleted', '!=', true));
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error: any) {
      throw new Error(`Failed to count persons: ${error.message}`);
    }
  }

  /**
   * Get sample of persons with birth dates for generation calculations
   * Used by dashboard for estimating family tree span
   *
   * @param {number} limitCount - Maximum number of persons to return
   * @returns {Promise<Person[]>} Sample of persons with birth dates
   * @throws {Error} If query fails
   */
  static async getPersonsWithBirthDates(limitCount: number = 1000): Promise<Person[]> {
    try {
      const personsRef = collection(db, this.COLLECTION_NAME);
      const q = query(
        personsRef,
        where('isDeleted', '==', false),
        where('birthDate', '!=', null),
        orderBy('birthDate'),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Person));
    } catch (error: any) {
      throw new Error(`Failed to get persons with birth dates: ${error.message}`);
    }
  }

  /**
   * Get incomplete profiles that need attention
   * Returns persons missing key biographical information
   *
   * @param {number} limitCount - Maximum number of profiles to return
   * @returns {Promise<Person[]>} Array of incomplete persons
   * @throws {Error} If query fails
   */
  static async getIncompleteProfiles(limitCount: number = 5): Promise<Person[]> {
    try {
      // Query recently updated persons to find incomplete ones
      const personsRef = collection(db, this.COLLECTION_NAME);
      const q = query(
        personsRef,
        where('isDeleted', '==', false),
        orderBy('updatedAt', 'desc'),
        limit(100) // Check last 100 updated for incomplete profiles
      );

      const querySnapshot = await getDocs(q);
      const persons = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Person));

      // Filter for incomplete profiles
      const incomplete = persons.filter(person => {
        const missingBiography = !person.biography || person.biography.trim() === '';
        const missingBirthDate = !person.birthDate;
        const missingBirthPlace = !person.birthPlace;
        return missingBiography || missingBirthDate || missingBirthPlace;
      });

      return incomplete.slice(0, limitCount);
    } catch (error: any) {
      throw new Error(`Failed to get incomplete profiles: ${error.message}`);
    }
  }

  /**
   * Get living persons with upcoming birthdays
   * Returns persons with birthdays in the next N days
   *
   * @param {number} days - Number of days to look ahead
   * @returns {Promise<Person[]>} Array of persons with upcoming birthdays
   * @throws {Error} If query fails
   */
  static async getUpcomingBirthdays(days: number = 30): Promise<Person[]> {
    try {
      // Query living persons with birth dates
      const personsRef = collection(db, this.COLLECTION_NAME);
      const q = query(
        personsRef,
        where('isDeleted', '==', false),
        where('isDeceased', '==', false),
        where('birthDate', '!=', null),
        orderBy('birthDate'),
        limit(500) // Reasonable limit for performance
      );

      const querySnapshot = await getDocs(q);
      const persons = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Person));

      const today = new Date();
      const upcoming: Array<Person & { daysUntil: number }> = [];

      persons.forEach(person => {
        if (!person.birthDate) return;

        const birthDate = new Date(person.birthDate);
        if (isNaN(birthDate.getTime())) return;

        // Calculate this year's birthday
        const thisYear = today.getFullYear();
        const birthdayThisYear = new Date(
          thisYear,
          birthDate.getMonth(),
          birthDate.getDate()
        );

        // If passed, check next year
        let nextBirthday = birthdayThisYear;
        if (birthdayThisYear < today) {
          nextBirthday = new Date(
            thisYear + 1,
            birthDate.getMonth(),
            birthDate.getDate()
          );
        }

        // Calculate days until
        const daysUntil = Math.ceil(
          (nextBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );

        // Add if within range
        if (daysUntil >= 0 && daysUntil <= days) {
          upcoming.push({ ...person, daysUntil });
        }
      });

      // Sort by days until birthday
      upcoming.sort((a, b) => a.daysUntil - b.daysUntil);

      return upcoming;
    } catch (error: any) {
      throw new Error(`Failed to get upcoming birthdays: ${error.message}`);
    }
  }
}

export default PersonService;